/**
 * Wo in einem Foto der Aufnahmeort steht — und wie man ihn überschreibt, ohne die
 * Datei umzubauen (ARCHITEKTUR.md 4.4).
 *
 * Übernommen aus `momentchen/lib/gps.ts` (648 Zeilen, seit 18.09.2026 im Einsatz).
 * **Gekürzt um die Videowege**: dieses Produkt kennt nur Bilder, und die
 * ISO-BMFF/QuickTime-Hälfte — `moov`, `udta`, `©xyz`, `com.apple.quicktime.location` —
 * wäre hier Code, den nie jemand ausführt. Was bleibt, ist Byte für Byte dasselbe:
 * JPEG, PNG, WebP, jeweils EXIF und XMP.
 *
 * ⚠️ Diese Datei importiert bewusst NICHTS und liegt bewusst **nicht** in `shared/`:
 * sie läuft nur im Browser und arbeitet mit `Blob`, also mit DOM-Typen, die
 * `tsconfig.server.json` nicht kennt. In `shared/` hätte sie den Typcheck des Servers
 * gebrochen — gemessen, nicht vermutet.
 *
 * ── Warum Flicken statt einer bereinigten Datei
 *
 * Zurückgegeben wird eine Liste `{ offset, bytes }`, keine neue Datei. Ein Flicken
 * **gleicher Länge** lässt die Dateigröße unangetastet — und an der Größe hängt hier
 * die Prüfung nach dem Hochladen (`server/app.ts`): der Server misst die echte Größe
 * im Speicher gegen das, was der Browser angekündigt hat. Jede fertige Bibliothek
 * hätte die Datei neu gebaut und dabei gekürzt (in `momentchen` am 18.09.2026
 * gemessen), und die Prüfung liefe ins Leere.
 *
 * Gelesen wird über `ByteReader` in Stücken, nie die ganze Datei auf einmal.
 */

/** Ein Stück Datei, ohne Annahme darüber, woher es kommt. */
export interface ByteReader {
  readonly size: number;
  read(offset: number, length: number): Promise<Uint8Array>;
}

/** Ein Bereich, der mit `bytes` überschrieben wird. Die Länge bleibt gleich. */
export interface Patch {
  offset: number;
  bytes: Uint8Array;
}

export function readerFromBlob(blob: Blob): ByteReader {
  return {
    size: blob.size,
    async read(offset, length) {
      const ende = Math.min(blob.size, offset + length);
      if (offset >= blob.size || ende <= offset) return new Uint8Array(0);
      return new Uint8Array(await blob.slice(offset, ende).arrayBuffer());
    },
  };
}

/** Für Tests und kleine Puffer, die ohnehin schon im Speicher liegen. */
export function readerFromBytes(bytes: Uint8Array): ByteReader {
  return {
    size: bytes.byteLength,
    read(offset, length) {
      const ende = Math.min(bytes.byteLength, offset + length);
      if (offset >= bytes.byteLength || ende <= offset) return Promise.resolve(new Uint8Array(0));
      return Promise.resolve(bytes.subarray(offset, ende));
    },
  };
}

/**
 * Ein Byte aus dem Puffer — außerhalb davon **0**.
 *
 * `noUncheckedIndexedAccess` ist in diesem Projekt an (in `momentchen`, aus dem der
 * Parser stammt, nicht), und ein `b[p]` ist damit `number | undefined`. Die Alternative
 * wäre ein `?? 0` an vier Dutzend Stellen gewesen; das hier sagt dieselbe Sache einmal.
 *
 * Dass 0 die richtige Antwort ist, hängt an der Arbeitsweise des Parsers: jede Stelle
 * prüft **vorher** die Länge, und wo doch über das Ende hinaus gelesen wird, soll der
 * Vergleich mit einem Magic Byte scheitern. 0 tut das, `undefined` in einer Rechnung
 * ergäbe `NaN` und damit einen Offset, dem niemand ansieht, dass er falsch ist.
 */
function byte(buf: Uint8Array, i: number): number {
  // `i` ist ein eigener Zähler über einen lokalen Puffer, nie ein Wert von außen.
  return buf[i] ?? 0;
}

/** Nullen der gewünschten Länge — der Flicken für einen Wertbereich. */
function nullen(offset: number, length: number): Patch {
  return { offset, bytes: new Uint8Array(length) };
}

function u16(b: Uint8Array, p: number, big: boolean): number {
  return big ? (byte(b, p) << 8) | byte(b, p + 1) : (byte(b, p + 1) << 8) | byte(b, p);
}

function u32(b: Uint8Array, p: number, big: boolean): number {
  return big
    ? ((byte(b, p) << 24) | (byte(b, p + 1) << 16) | (byte(b, p + 2) << 8) | byte(b, p + 3)) >>> 0
    : ((byte(b, p + 3) << 24) | (byte(b, p + 2) << 16) | (byte(b, p + 1) << 8) | byte(b, p)) >>> 0;
}

/** Bytes je TIFF-Typ. 0 heißt: Typ unbekannt, Wertlänge nicht berechenbar. */
function typGroesse(typ: number): number {
  switch (typ) {
    case 1: // BYTE
    case 2: // ASCII
    case 6: // SBYTE
    case 7: // UNDEFINED
      return 1;
    case 3: // SHORT
    case 8: // SSHORT
      return 2;
    case 4: // LONG
    case 9: // SLONG
    case 11: // FLOAT
      return 4;
    case 5: // RATIONAL
    case 10: // SRATIONAL
    case 12: // DOUBLE
      return 8;
    default:
      return 0;
  }
}

const IFD_EINTRAG = 12;
/** IFD0-Tag, dessen Wert auf das GPS-IFD zeigt (CIPA DC-008 4.6.3). */
const TAG_GPS_IFD = 0x8825;

/**
 * Das GPS-IFD leeren — der Kern der ganzen Sache.
 *
 * ⚠️ Es genügt **nicht**, die 12-Byte-Einträge zu nullen. Auslesewerkzeuge finden
 * danach zwar nichts mehr, aber die Koordinaten stehen unverändert in der Datei: ein
 * RATIONAL passt nicht in die vier Byte des Eintrags und liegt deshalb über einen
 * Offset woanders. In `momentchen` an `@xoi/gps-metadata-remover` gemessen
 * (18.09.2026) — exifr las kein GPS mehr, `53/1` stand weiter drin. Wer dem Offset
 * nicht folgt, baut eine Lücke, die jede Prüfung besteht und einen Menschen verrät.
 *
 * Der Zeiger in IFD0 bleibt stehen und zeigt auf ein **leeres** IFD (Anzahl 0). Das ist
 * die kleinste Änderung, die trägt: an der Struktur der Datei ändert sich nichts, es
 * ist nur nichts mehr darin.
 */
function leereGpsIfd(
  tiff: Uint8Array,
  tiffBasis: number,
  ifdOffset: number,
  big: boolean,
): Patch[] {
  if (ifdOffset <= 0 || ifdOffset + 2 > tiff.byteLength) return [];
  const anzahl = u16(tiff, ifdOffset, big);
  // Ein IFD mit absurd vielen Einträgen ist keine Struktur, die wir verstehen.
  if (anzahl === 0 || ifdOffset + 2 + anzahl * IFD_EINTRAG > tiff.byteLength) return [];

  const flicken: Patch[] = [];
  for (let i = 0; i < anzahl; i++) {
    const e = ifdOffset + 2 + i * IFD_EINTRAG;
    const typ = u16(tiff, e + 2, big);
    const zahl = u32(tiff, e + 4, big);
    const groesse = typGroesse(typ);
    if (groesse === 0) continue;
    const laenge = groesse * zahl;
    // Über vier Byte liegt der Wert außerhalb des Eintrags — dorthin folgen.
    if (laenge > 4) {
      const wertOffset = u32(tiff, e + 8, big);
      if (wertOffset > 0 && wertOffset + laenge <= tiff.byteLength) {
        flicken.push(nullen(tiffBasis + wertOffset, laenge));
      }
    }
  }
  // Die Einträge selbst und die Anzahl: erst danach, damit das Lesen oben auf den
  // unveränderten Bytes arbeitet.
  flicken.push(nullen(tiffBasis + ifdOffset + 2, anzahl * IFD_EINTRAG));
  flicken.push(nullen(tiffBasis + ifdOffset, 2));
  return flicken;
}

/**
 * Ein TIFF-Block (EXIF) → die Flicken, die den Ort daraus entfernen.
 *
 * `tiffBasis` ist der Offset des TIFF-Headers **in der Datei**; alle Offsets im Block
 * sind relativ dazu. Die Byte-Reihenfolge steht in seinen ersten zwei Bytes und gilt
 * für alles darin — falsch gelesen zeigt jeder Offset ins Leere.
 */
export function gpsPatchesInTiff(tiff: Uint8Array, tiffBasis: number): Patch[] {
  if (tiff.byteLength < 8) return [];
  const m0 = byte(tiff, 0);
  const m1 = byte(tiff, 1);
  const big = m0 === 0x4d && m1 === 0x4d; // "MM"
  const little = m0 === 0x49 && m1 === 0x49; // "II"
  if (!big && !little) return [];
  if (u16(tiff, 2, big) !== 42) return [];

  const ifd0 = u32(tiff, 4, big);
  if (ifd0 <= 0 || ifd0 + 2 > tiff.byteLength) return [];
  const anzahl = u16(tiff, ifd0, big);
  if (ifd0 + 2 + anzahl * IFD_EINTRAG > tiff.byteLength) return [];

  for (let i = 0; i < anzahl; i++) {
    const e = ifd0 + 2 + i * IFD_EINTRAG;
    if (u16(tiff, e, big) !== TAG_GPS_IFD) continue;
    return leereGpsIfd(tiff, tiffBasis, u32(tiff, e + 8, big), big);
  }
  return [];
}

const XMP_NS = "http://ns.adobe.com/xap/1.0/";

/**
 * Ortsangaben in einem XMP-Block mit Leerzeichen **gleicher Länge** überschreiben.
 *
 * XMP ist Text in der Datei, kein Binärbaum — der Block bleibt wohlgeformtes XML, nur
 * die Werte sind leer. Gesucht wird ausschließlich innerhalb des Blocks, damit ein
 * Treffer nie in den Bilddaten landet.
 */
export function gpsPatchesInXmp(block: Uint8Array, basis: number): Patch[] {
  const text = new TextDecoder("utf-8", { fatal: false }).decode(block);
  if (!text.includes(XMP_NS)) return [];
  const flicken: Patch[] = [];
  // `exif:GPSLatitude="…"` und die Geschwister, auch als Element geschrieben.
  const muster = /exif:GPS[A-Za-z]*\s*=\s*"([^"]*)"|<exif:GPS[A-Za-z]*>([^<]*)</g;
  let treffer: RegExpExecArray | null;
  while ((treffer = muster.exec(text)) !== null) {
    // Der Typ lügt hier: TypeScript gibt Fanggruppen als `string` aus, zur Laufzeit
    // ist die nicht getroffene Alternative `undefined`. Ohne diese Umdeutung fiele
    // die Prüfung darunter als „unnötig" aus dem Lint — und wäre trotzdem nötig.
    const gruppen = treffer as unknown as (string | undefined)[];
    const wert = gruppen[1] ?? gruppen[2];
    if (wert === undefined || wert.length === 0) continue;
    const ganzerTreffer = gruppen[0] ?? "";
    const imText = treffer.index + ganzerTreffer.lastIndexOf(wert);
    // ⚠️ Zeichen- und Byte-Offsets sind **nicht** dasselbe, und hier zeigt der Flicken
    // in die Bytes. Ein standardkonformes XMP-Paket beginnt mit
    // `<?xpacket begin="U+FEFF" …?>` — das BOM ist ein Zeichen aus drei Bytes, und ab
    // da läge jeder aus der Zeichenzahl gerechnete Offset daneben. In `momentchen`
    // stand hier erst ein Schutz, der bei jedem Mehrbyte-Zeichen aufgab; da praktisch
    // jedes echte XMP dieses BOM hat, war der ganze Zweig damit tot.
    const kodierer = new TextEncoder();
    const vorWert = kodierer.encode(text.slice(0, imText)).length;
    const wertBytes = kodierer.encode(wert).length;
    flicken.push({ offset: basis + vorWert, bytes: kodierer.encode(" ".repeat(wertBytes)) });
  }
  return flicken;
}

/**
 * CRC-32 wie in PNG. Gerechnet wird über Chunk-Typ **und** Daten, nicht über das
 * Längenfeld (W3C PNG § 5.3). Wer sie nach dem Überschreiben nicht neu rechnet,
 * hinterlässt eine Datei, die strenge Leser als kaputt abweisen.
 *
 * Ohne Tabelle: die Flicken sind ein paar Dutzend Byte, eine 1-KiB-Tabelle im Bündel
 * wäre teurer als die Schleife.
 */
export function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (const b of bytes) {
    c ^= b;
    for (let k = 0; k < 8; k++) c = c & 1 ? (c >>> 1) ^ 0xedb88320 : c >>> 1;
  }
  return (c ^ 0xffffffff) >>> 0;
}

function u32Bytes(n: number): Uint8Array {
  return Uint8Array.from([(n >>> 24) & 0xff, (n >>> 16) & 0xff, (n >>> 8) & 0xff, n & 0xff]);
}

/** Die Flicken auf einen lokalen Puffer legen — für die CRC-Rechnung. */
function lokalGeflickt(daten: Uint8Array, basis: number, flicken: readonly Patch[]): Uint8Array {
  const out = new Uint8Array(daten);
  for (const f of flicken) out.set(f.bytes, f.offset - basis);
  return out;
}

const PNG_MAGIC = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

/**
 * PNG: der `eXIf`-Chunk trägt den TIFF-Block **direkt** — ohne `Exif\0\0` davor
 * (W3C PNG § 11.3.4.5). Laut Spezifikation darf es nur einen geben.
 */
async function pngPatches(reader: ByteReader): Promise<Patch[]> {
  let p = 8; // hinter der Signatur
  while (p + 8 <= reader.size) {
    const kopf = await reader.read(p, 8);
    if (kopf.byteLength < 8) break;
    const laenge = u32(kopf, 0, true);
    const typ = String.fromCharCode(byte(kopf, 4), byte(kopf, 5), byte(kopf, 6), byte(kopf, 7));
    const daten = p + 8;
    if (typ === "IDAT" || typ === "IEND") break; // ab hier keine Metadaten mehr
    if (typ === "eXIf" && laenge > 0 && daten + laenge <= reader.size) {
      const block = await reader.read(daten, laenge);
      const flicken = gpsPatchesInTiff(block, daten);
      if (flicken.length > 0) {
        const neu = lokalGeflickt(block, daten, flicken);
        const fuerCrc = new Uint8Array(4 + neu.byteLength);
        fuerCrc.set(kopf.subarray(4, 8), 0);
        fuerCrc.set(neu, 4);
        flicken.push({ offset: daten + laenge, bytes: u32Bytes(crc32(fuerCrc)) });
      }
      return flicken;
    }
    p = daten + laenge + 4; // Daten plus CRC
  }
  return [];
}

/**
 * Steht vor diesem Block die Zeichenfolge `Exif`?
 *
 * In JPEG gehört sie dazu (`Exif\0\0`, dann der TIFF-Header); in WebP schreiben sie
 * manche Erzeuger und die Spezifikation nicht. Eine Funktion für beide, damit die
 * beiden Vergleiche nicht auseinanderlaufen.
 */
function istExifMarke(block: Uint8Array): boolean {
  return (
    byte(block, 0) === 0x45 &&
    byte(block, 1) === 0x78 &&
    byte(block, 2) === 0x69 &&
    byte(block, 3) === 0x66
  );
}

const WEBP_EXIF = "EXIF";
const WEBP_XMP = "XMP ";

/**
 * WebP: `EXIF`- und `XMP `-Chunk im RIFF. Keine Prüfsumme, aber ungerade Chunks sind
 * auf gerade Länge aufgefüllt — das Füllbyte gehört nicht zur Größe.
 */
async function webpPatches(reader: ByteReader): Promise<Patch[]> {
  const flicken: Patch[] = [];
  let p = 12; // hinter "RIFF" + Größe + "WEBP"
  while (p + 8 <= reader.size) {
    const kopf = await reader.read(p, 8);
    if (kopf.byteLength < 8) break;
    const typ = String.fromCharCode(byte(kopf, 0), byte(kopf, 1), byte(kopf, 2), byte(kopf, 3));
    const laenge = u32(kopf, 4, false); // RIFF ist little endian
    const daten = p + 8;
    if (laenge > 0 && daten + laenge <= reader.size) {
      const block = await reader.read(daten, laenge);
      if (typ === WEBP_EXIF) {
        // Manche Erzeuger schreiben "Exif\0\0" davor, die Spezifikation nicht.
        const mitPraefix = istExifMarke(block);
        const v = mitPraefix ? 6 : 0;
        flicken.push(...gpsPatchesInTiff(block.subarray(v), daten + v));
      } else if (typ === WEBP_XMP) {
        flicken.push(...gpsPatchesInXmp(block, daten));
      }
    }
    p = daten + laenge + (laenge % 2); // Füllbyte bei ungerader Länge
  }
  return flicken;
}

const JPEG_SOI = 0xd8;
const JPEG_SOS = 0xda;
const JPEG_APP1 = 0xe1;
/** Wie viel eines Segments gelesen wird, um seine Art zu erkennen. */
const KOPF = 4;

/**
 * JPEG: die APP1-Segmente durchgehen, EXIF und XMP behandeln, bei SOS aufhören.
 *
 * Ab SOS beginnen die Bilddaten; dahinter steht keine Metadatenstruktur mehr, und ein
 * Muster, das dort zufällig passt, wäre ein Treffer mitten im Bild.
 */
async function jpegPatches(reader: ByteReader): Promise<Patch[]> {
  const flicken: Patch[] = [];
  let p = 2; // hinter SOI
  while (p + KOPF <= reader.size) {
    const kopf = await reader.read(p, KOPF);
    if (kopf.byteLength < KOPF || byte(kopf, 0) !== 0xff) break;
    const marker = byte(kopf, 1);
    if (marker === JPEG_SOS) break;
    const laenge = (byte(kopf, 2) << 8) | byte(kopf, 3);
    if (laenge < 2) break;
    const inhalt = p + 2 + 2; // hinter Marker und Längenfeld
    const inhaltLaenge = laenge - 2;

    if (marker === JPEG_APP1 && inhaltLaenge > 6) {
      const block = await reader.read(inhalt, inhaltLaenge);
      const istExif = istExifMarke(block);
      if (istExif) {
        // Hinter "Exif\0\0" beginnt der TIFF-Header.
        flicken.push(...gpsPatchesInTiff(block.subarray(6), inhalt + 6));
      } else {
        flicken.push(...gpsPatchesInXmp(block, inhalt));
      }
    }
    p += 2 + laenge;
  }
  return flicken;
}

/**
 * Die Dateiarten, in denen der Aufnahmeort überhaupt gesucht wird — die eine Liste
 * dazu, damit niemand sie an zwei Stellen pflegt.
 *
 * ⚠️ Der Unterschied zu „nichts gefunden" ist tragend: bei **HEIC/HEIF wird gar nicht
 * gesucht**. Das Format ist ISO-BMFF und hier nicht gebaut. Ein iPhone-Foto in HEIC
 * geht also mit seinem Aufnahmeort zu Anna. Was daraus folgt, steht in
 * `shared/keys.ts` bei `ALLOWED_CONTENT_TYPES` und als Entscheidung in `TODO.md`.
 */
const LOCATABLE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

export function canFindLocation(contentType: string): boolean {
  return LOCATABLE_TYPES.has(contentType);
}

/**
 * Die Ortsbytes einer Datei — oder eine leere Liste, wenn keine gefunden wurden.
 *
 * **Wirft nie.** Eine Datei, deren Struktur wir nicht verstehen, geht trotzdem hoch —
 * dieselbe Entscheidung wie in `momentchen` (18.09.2026): sonst verlöre die Kundschaft
 * Fotos wegen eines Fehlers in **unserem** Parser. Der Preis gehört in Annas
 * Datenschutzerklärung: zugesagt wird „wo wir ihn finden", nicht „immer".
 */
export async function findLocationPatches(
  reader: ByteReader,
  contentType: string,
): Promise<Patch[]> {
  if (!canFindLocation(contentType)) return [];
  try {
    if (contentType === "image/jpeg") {
      const soi = await reader.read(0, 2);
      if (soi.byteLength < 2 || byte(soi, 0) !== 0xff || byte(soi, 1) !== JPEG_SOI) return [];
      return await jpegPatches(reader);
    }
    if (contentType === "image/png") {
      const magie = await reader.read(0, 8);
      if (magie.byteLength < 8 || PNG_MAGIC.some((b, i) => byte(magie, i) !== b)) return [];
      return await pngPatches(reader);
    }
    if (contentType === "image/webp") {
      const kopf = await reader.read(0, 12);
      if (kopf.byteLength < 12) return [];
      const riff = String.fromCharCode(byte(kopf, 0), byte(kopf, 1), byte(kopf, 2), byte(kopf, 3));
      const webp = String.fromCharCode(
        byte(kopf, 8),
        byte(kopf, 9),
        byte(kopf, 10),
        byte(kopf, 11),
      );
      if (riff !== "RIFF" || webp !== "WEBP") return [];
      return await webpPatches(reader);
    }
    return [];
  } catch {
    return [];
  }
}

/**
 * Die Flicken auf einen Blob legen, ohne ihn zu kopieren.
 *
 * `Blob.slice` materialisiert nichts — das Ergebnis ist eine Sicht auf dieselben
 * Bytes. Ein großes Foto kostet hier also weder Speicher noch Zeit, und die Größe
 * bleibt exakt gleich.
 */
export function applyPatches(file: Blob, patches: readonly Patch[]): Blob {
  if (patches.length === 0) return file;
  const sortiert = [...patches].sort((a, b) => a.offset - b.offset);
  const teile: BlobPart[] = [];
  let p = 0;
  for (const flicken of sortiert) {
    // Überlappende oder rückwärts liegende Flicken würden die Länge verschieben.
    if (flicken.offset < p || flicken.offset + flicken.bytes.byteLength > file.size) continue;
    if (flicken.offset > p) teile.push(file.slice(p, flicken.offset));
    // Eine `Uint8Array` über einem `ArrayBufferLike` ist kein `BlobPart` — eine eigene
    // über einem echten `ArrayBuffer` schon. Flicken sind klein, die Kopie kostet nichts.
    const kopie = new Uint8Array(flicken.bytes.byteLength);
    kopie.set(flicken.bytes);
    teile.push(kopie.buffer);
    p = flicken.offset + flicken.bytes.byteLength;
  }
  if (p < file.size) teile.push(file.slice(p));
  return new Blob(teile, { type: file.type });
}
